import { sanitizeCustomHtml } from "@redotech/order-tracking-common/sanitize-custom-html";
import {
  DEFAULT_SMS_CONSENT_DISCLOSURE_LINE_HEIGHT,
  DEFAULT_TRACKING_UPDATES_CONTENT_POSITION,
  TrackingPageTrackingUpdatesBlock,
  TrackingUpdatesContentPosition,
} from "@redotech/order-tracking-common/tracking-page-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import BellIcon from "@redotech/redo-icons/bell-03.svg";
import { DEFAULT_EMAIL_LINK_COLOR } from "@redotech/redo-marketing-templates-common/email-builder";
import htmlReactParser from "html-react-parser";
import { memo, useState } from "react";
import { toast } from "../../alert";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "../../arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import { ButtonSize } from "../../arbiter-components/redo-button-close-x";
import { Flex } from "../../flex";
import { Text } from "../../text";
import {
  TrackingPageAccessMode,
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import {
  getBodyStyles,
  getButtonBorderRadius,
  getButtonHierarchy,
  getButtonStyles,
  getHeaderStyles,
  getLabelStyles,
  textBlockContentStyles,
} from "./shared/block-styles";
import { BlockWrapper } from "./shared/block-wrapper";

const CONTENT_JUSTIFY = {
  [TrackingUpdatesContentPosition.TOP]: "flex-start",
  [TrackingUpdatesContentPosition.MIDDLE]: "center",
  [TrackingUpdatesContentPosition.BOTTOM]: "flex-end",
  [TrackingUpdatesContentPosition.JUSTIFIED]: "space-between",
} as const;

export function contentPositionJustify(
  position: TrackingUpdatesContentPosition | undefined,
): (typeof CONTENT_JUSTIFY)[TrackingUpdatesContentPosition] {
  return CONTENT_JUSTIFY[position ?? DEFAULT_TRACKING_UPDATES_CONTENT_POSITION];
}

export const TrackingUpdates = memo(function TrackingUpdates({
  _id,
  title,
  buttonSettings,
  smsConsentDisclosure,
  smsConsentLabelFontSize,
  smsConsentDisclosureLineHeight,
  contentPosition,
}: TrackingPageTrackingUpdatesBlock) {
  const context = useRequiredContext(TrackingPageContext);
  const { t } = useTranslation();
  const { pageSettings, environment } = context;
  const trackingUpdates =
    environment === TrackingPageEnvironment.CUSTOMER_PORTAL
      ? context.trackingUpdates
      : undefined;
  const marketingConsentSubmittable =
    environment === TrackingPageEnvironment.CUSTOMER_PORTAL &&
    context.accessMode === TrackingPageAccessMode.AUTHENTICATED;
  // Merchants write the disclosure around this checkbox, so the builder
  // previews it; consent is only ever submitted from the authenticated portal.
  const showMarketingConsent =
    environment === TrackingPageEnvironment.BUILDER ||
    marketingConsentSubmittable;

  const [phoneNumber, setPhoneNumber] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSignedUp, setIsSignedUp] = useState(false);

  const headerStyles = getHeaderStyles(pageSettings);
  const { fontSize: _, ...trackingUpdatesHeaderStyles } = headerStyles;
  const bodyStyles = getBodyStyles(pageSettings);
  const disclosureId = `tracking-updates-disclosure-${_id}`;
  const linkColor =
    pageSettings?.typography.linkFontColor ?? DEFAULT_EMAIL_LINK_COLOR;
  const labelStyles = getLabelStyles(pageSettings);
  const consentLabelStyle =
    smsConsentLabelFontSize === undefined
      ? bodyStyles
      : {
          ...bodyStyles,
          fontSize: `${smsConsentLabelFontSize}px`,
          lineHeight: 1.5,
        };
  const buttonStyles = getButtonStyles(
    pageSettings?.pageButtonSettings,
    buttonSettings,
  );
  const buttonHierarchy = getButtonHierarchy(buttonSettings);
  const buttonBorderRadius = getButtonBorderRadius(pageSettings);

  const submit = useHandler(async () => {
    setIsSubmitting(true);
    const result = await trackingUpdates?.submit({
      phoneNumber,
      marketingConsent: marketingConsentSubmittable && marketingConsent,
    });
    if (!result?.success) {
      toast(result?.error ?? t("Failed to sign up for tracking updates"), {
        variant: "error",
      });
    } else {
      setIsSignedUp(true);
      toast(t("Signed up for tracking updates"), { variant: "success" });
    }
    setIsSubmitting(false);
  });

  const justified =
    (contentPosition ?? DEFAULT_TRACKING_UPDATES_CONTENT_POSITION) ===
    TrackingUpdatesContentPosition.JUSTIFIED;
  const formGroup = (
    <Flex dir="column" gap="md">
      <Flex align="center" dir="row" gap="md">
        <RedoTextInput
          fullWidth
          placeholder={t("Enter your phone number")}
          placeholderStyle={labelStyles}
          setValue={setPhoneNumber}
          value={phoneNumber}
        />
        <RedoButton
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
            ...buttonStyles,
            ...buttonBorderRadius,
          }}
          hierarchy={buttonHierarchy}
          onClick={submit}
          pending={isSubmitting}
          size={ButtonSize.SMALL}
          text={t("Sign up")}
        />
      </Flex>
      {showMarketingConsent && (
        <RedoCheckbox
          label={t("Text me with news and offers")}
          labelStyle={consentLabelStyle}
          setValue={(value) => setMarketingConsent(value === true)}
          size="xs"
          value={marketingConsent}
        />
      )}
    </Flex>
  );
  const disclosure = smsConsentDisclosure && (
    <>
      <style>
        {textBlockContentStyles({
          containerId: disclosureId,
          linkColor,
          pageSettings,
        })}
      </style>
      {/* `Text` drops `id`, so the rules above would bind to
            nothing; `Flex` forwards it like the text block does. */}
      <Flex dir="column" gap="none" id={disclosureId}>
        <Text
          as="div"
          fontSize="xs"
          style={{
            ...bodyStyles,
            lineHeight:
              smsConsentDisclosureLineHeight ??
              DEFAULT_SMS_CONSENT_DISCLOSURE_LINE_HEIGHT,
          }}
        >
          {htmlReactParser(sanitizeCustomHtml(smsConsentDisclosure))}
        </Text>
      </Flex>
    </>
  );

  return (
    <BlockWrapper
      environment={environment}
      loading={false}
      pageSettings={pageSettings}
    >
      <Flex
        dir="column"
        flex={1}
        gap="xl"
        justify={justified ? "space-between" : "flex-start"}
        p="3xl"
      >
        <Flex align="center" dir="row" gap="md">
          <div style={trackingUpdatesHeaderStyles}>
            <BellIcon />
          </div>
          <Text
            fontSize="lg"
            fontWeight="semibold"
            style={trackingUpdatesHeaderStyles}
          >
            {title}
          </Text>
        </Flex>
        {isSignedUp ? (
          <Text fontSize="xs">
            {t(
              "You've been signed up for tracking updates. You'll receive text messages throughout the delivery process.",
            )}
          </Text>
        ) : // Justified spreads title, form, and disclosure evenly, so they
        // have to be siblings of the title rather than a nested group.
        justified ? (
          <>
            {formGroup}
            {disclosure}
          </>
        ) : (
          <Flex
            dir="column"
            flex={1}
            gap="xl"
            justify={contentPositionJustify(contentPosition)}
          >
            {formGroup}
            {disclosure}
          </Flex>
        )}
      </Flex>
    </BlockWrapper>
  );
});
